import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Stack,
  Grid
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { 
  Invoice, 
  InvoiceItem, 
  InvoiceStatus, 
  PaymentMethod,
  createInvoice, 
  getInvoiceById, 
  updateInvoice,
  generateInvoiceNumber
} from '../firebase/invoiceServices';
import { getRevenueGLAccounts } from '../firebase/glTransactionServices';
import { GLAccount } from '../firebase/glAccountServices';
import { Timestamp } from 'firebase/firestore';

// Convert Firestore Timestamp to Date for DatePicker
const convertToDate = (value: Date | Timestamp | null): Date | null => {
  if (!value) return null;
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  return value;
};

// Status options
const statusOptions: InvoiceStatus[] = [
  'draft',
  'sent',
  'paid',
  'overdue',
  'cancelled'
];

// Payment method options
const paymentMethodOptions: PaymentMethod[] = [
  'Bank Transfer',
  'Credit Card',
  'Cash',
  'Direct Debit',
  'Other'
];

const InvoiceEntry: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isEditMode = Boolean(id);

  // State for invoice data
  const [invoice, setInvoice] = useState<Invoice>({
    invoiceNumber: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    amount: 0,
    status: 'draft' as InvoiceStatus,
    issueDate: new Date(),
    dueDate: new Date(new Date().setDate(new Date().getDate() + 30)), // Default due date: 30 days from now
    items: [],
    notes: '',
    terms: 'Payment is due within 30 days of invoice date.'
  });

  // State for loading and error
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [glAccounts, setGLAccounts] = useState<GLAccount[]>([]);

  // Fetch invoice data if in edit mode
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!id) return;

      try {
        const invoiceData = await getInvoiceById(id);
        if (invoiceData) {
          setInvoice(invoiceData);
        } else {
          setError('Invoice not found');
        }
      } catch (error) {
        console.error('Error fetching invoice:', error);
        setError('Failed to load invoice data');
      } finally {
        setLoading(false);
      }
    };

    // Fetch GL accounts for revenue
    const fetchGLAccounts = async () => {
      try {
        const accounts = await getRevenueGLAccounts();
        setGLAccounts(accounts);
      } catch (error) {
        console.error('Error fetching GL accounts:', error);
      }
    };

    fetchInvoiceData();
    fetchGLAccounts();
  }, [id]);

  // Generate invoice number for new invoices
  useEffect(() => {
    const generateNumber = async () => {
      if (!isEditMode && !invoice.invoiceNumber) {
        try {
          const number = await generateInvoiceNumber();
          setInvoice(prev => ({ ...prev, invoiceNumber: number }));
        } catch (error) {
          console.error('Error generating invoice number:', error);
        }
      }
    };

    generateNumber();
  }, [isEditMode, invoice.invoiceNumber]);

  // Calculate total amount whenever items change
  useEffect(() => {
    const total = invoice.items.reduce((sum, item) => sum + (item.amount || item.quantity * item.unitPrice), 0);
    setInvoice(prev => ({ ...prev, amount: total }));
  }, [invoice.items]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setInvoice(prev => ({ ...prev, [name]: value }));
  };

  // Handle status change
  const handleStatusChange = (e: SelectChangeEvent<string>) => {
    setInvoice(prev => ({ ...prev, status: e.target.value as InvoiceStatus }));
  };

  // Handle payment method change
  const handlePaymentMethodChange = (e: SelectChangeEvent<string>) => {
    setInvoice(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }));
  };

  // Handle date change
  const handleDateChange = (name: string, date: Date | null) => {
    if (date) {
      setInvoice(prev => ({ ...prev, [name]: date }));
    }
  };

  // Add new item
  const handleAddItem = () => {
    const newItem: InvoiceItem = {
      id: uuidv4(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      amount: 0
    };

    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  // Update item
  const handleItemChange = (id: string, field: keyof InvoiceItem, value: any) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          
          // Recalculate amount if quantity or unitPrice changed
          if (field === 'quantity' || field === 'unitPrice') {
            updatedItem.amount = updatedItem.quantity * updatedItem.unitPrice;
          }
          
          return updatedItem;
        }
        return item;
      })
    }));
  };

  // Delete item
  const handleDeleteItem = (id: string) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }));
  };

  // Save invoice
  const handleSave = async () => {
    // Validate required fields
    if (!invoice.customerName) {
      enqueueSnackbar('Customer name is required', { variant: 'error' });
      return;
    }

    if (invoice.items.length === 0) {
      enqueueSnackbar('At least one item is required', { variant: 'error' });
      return;
    }

    for (const item of invoice.items) {
      if (!item.description) {
        enqueueSnackbar('All items must have a description', { variant: 'error' });
        return;
      }
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditMode && id) {
        await updateInvoice(id, invoice);
        enqueueSnackbar('Invoice updated successfully', { variant: 'success' });
      } else {
        const newId = await createInvoice(invoice);
        enqueueSnackbar('Invoice created successfully', { variant: 'success' });
      }
      navigate('/invoices');
    } catch (error) {
      console.error('Error saving invoice:', error);
      setError('Failed to save invoice. Please try again.');
      enqueueSnackbar('Failed to save invoice', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/invoices')} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Invoice'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Invoice Details
          </Typography>
          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
            <Box sx={{ width: { xs: '100%', sm: '45%', md: '22%' } }}>
              <TextField
                label="Invoice Number"
                name="invoiceNumber"
                value={invoice.invoiceNumber}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true, // Invoice number is auto-generated
                }}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '45%', md: '22%' } }}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={invoice.status}
                  onChange={handleStatusChange}
                  label="Status"
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '45%', md: '22%' } }}>
              <DatePicker
                label="Issue Date"
                value={convertToDate(invoice.issueDate)}
                onChange={(date) => handleDateChange('issueDate', date)}
                slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '45%', md: '22%' } }}>
              <DatePicker
                label="Due Date"
                value={convertToDate(invoice.dueDate)}
                onChange={(date) => handleDateChange('dueDate', date)}
                slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
              />
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Customer Information
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 3 }}>
            <Box>
              <TextField
                label="Customer Name"
                name="customerName"
                value={invoice.customerName}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField
                label="Customer Email"
                name="customerEmail"
                value={invoice.customerEmail || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                type="email"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField
                label="Customer Phone"
                name="customerPhone"
                value={invoice.customerPhone || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Invoice Items
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
            >
              Add Item
            </Button>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">GL Account</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" sx={{ py: 2 }}>
                        No items added yet. Click "Add Item" to add an invoice item.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  invoice.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <TextField
                          value={item.description}
                          onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                          fullWidth
                          variant="standard"
                          placeholder="Item description"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.id, 'quantity', Number(e.target.value))}
                          variant="standard"
                          inputProps={{ min: 1, style: { textAlign: 'right' } }}
                          sx={{ width: '80px' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(item.id, 'unitPrice', Number(e.target.value))}
                          variant="standard"
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                          sx={{ width: '100px' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.amount || item.quantity * item.unitPrice)}
                      </TableCell>
                      <TableCell align="right">
                        <FormControl variant="standard" sx={{ minWidth: 120 }}>
                          <Select
                            value={item.glAccountId || ''}
                            onChange={(e) => handleItemChange(item.id, 'glAccountId', e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">
                              <em>Default</em>
                            </MenuItem>
                            {glAccounts.map((account) => (
                              <MenuItem key={account.id} value={account.id}>
                                {account.accountNumber} - {account.accountName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          color="error" 
                          onClick={() => handleDeleteItem(item.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {invoice.items.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                      Total:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(invoice.amount)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {invoice.status === 'paid' && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Information
            </Typography>
            <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
              <Box sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}>
                <DatePicker
                  label="Payment Date"
                  value={convertToDate(invoice.paymentDate || null)}
                  onChange={(date) => handleDateChange('paymentDate', date)}
                  slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="payment-method-label">Payment Method</InputLabel>
                  <Select
                    labelId="payment-method-label"
                    name="paymentMethod"
                    value={invoice.paymentMethod || ''}
                    onChange={handlePaymentMethodChange}
                    label="Payment Method"
                  >
                    {paymentMethodOptions.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Paper>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Additional Information
          </Typography>
          <Stack direction="column" spacing={3}>
            <Box sx={{ width: '100%' }}>
              <TextField
                label="Notes"
                name="notes"
                value={invoice.notes || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                placeholder="Add any notes or comments about this invoice"
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              <TextField
                label="Terms and Conditions"
                name="terms"
                value={invoice.terms || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                placeholder="Payment terms and conditions"
              />
            </Box>
          </Stack>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/invoices')}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Invoice'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default InvoiceEntry;
